import React, { useState, useCallback, useRef, useEffect } from 'react';
import './CricketGame.css';

/* ═══════════════════════════════════════════════════════════════
   CONSTANTES
═══════════════════════════════════════════════════════════════ */

/** Orden del tablero de cricket (de arriba abajo) */
const TARGETS = [20, 19, 18, 17, 16, 15, 'bull'];

/** Valor en puntos de cada marca que supera el cierre */
const POINT_VALUE = { 20: 20, 19: 19, 18: 18, 17: 17, 16: 16, 15: 15, bull: 25 };

/** Etiqueta visual de cada objetivo */
const LABEL = { 20: '20', 19: '19', 18: '18', 17: '17', 16: '16', 15: '15', bull: 'BULL' };

/** Nombres de multiplicadores */
const MULT_NAME = { 1: 'Simple', 2: 'Doble', 3: 'Triple' };

/**
 * Colores de la diana real por objetivo.
 * single = zona principal del sector (crema o negro)
 * multi  = doble y triple (rojo o verde)
 */
const BOARD_COLOR = {
  20:   { single: 'black', multi: 'red'   },
  19:   { single: 'cream', multi: 'green' },
  18:   { single: 'black', multi: 'red'   },
  17:   { single: 'cream', multi: 'green' },
  16:   { single: 'cream', multi: 'green' },
  15:   { single: 'cream', multi: 'green' },
  bull: { single: 'green', multi: 'red'   },
};

/* ═══════════════════════════════════════════════════════════════
   FUNCIONES PURAS DE JUEGO
═══════════════════════════════════════════════════════════════ */

/** Inicializa el objeto de marcas con 0 en cada objetivo */
export const createMarks = () => Object.fromEntries(TARGETS.map(t => [t, 0]));

/** El jugador tiene ≥3 marcas en ese objetivo → cerrado */
const isClosed = (marks, t) => (marks[t] || 0) >= 3;

/** Los dos jugadores han cerrado → bloqueado (nadie puede puntuar) */
const isLocked = (m0, m1, t) => isClosed(m0, t) && isClosed(m1, t);

/** El jugador ha cerrado todos los objetivos */
export const allClosed = marks => TARGETS.every(t => isClosed(marks, t));

/** MPR (marcas por cada tres dardos), incluyendo los dardos reales de un cierre parcial. */
export const calculateMpr = (player, partialTurnDarts = 0) => {
  const dartsThrown = player.roundsPlayed * 3 + partialTurnDarts;
  return dartsThrown > 0 ? ((player.marksTotal / dartsThrown) * 3).toFixed(2) : '-';
};

/**
 * Símbolo visual de marcas:
 *   0 → '' (vacío)   1 → '/'   2 → 'X'   ≥3 → '⊗' (cerrado)
 */
const markSymbol = n => {
  if (!n || n === 0) return '';
  if (n === 1) return '/';
  if (n === 2) return 'X';
  return '⊗';
};

/**
 * Procesa un lanzamiento y devuelve el resultado.
 * Fórmula de puntuación:
 *   marcas_que_puntúan = max(0, nuevo−3) − max(0, anterior−3)
 * En duelo solo puntúan si el rival no ha cerrado el objetivo. En individual,
 * todas las marcas extra siguen puntuando hasta que se cierre el tablero.
 *
 * @param {Object} cpMarks   Marcas actuales del jugador que tira
 * @param {number} cpScore   Puntuación actual del jugador que tira
 * @param {Object} oppMarks  Marcas del rival
 * @param {string|number} target  Objetivo (20..15 | 'bull')
 * @param {number} mult      Multiplicador (1=simple, 2=doble, 3=triple)
 * @returns {{ newMarks, newScore, pts, closedNow, lockedNow }}
 */
export const applyThrow = (cpMarks, cpScore, oppMarks, target, mult) => {
  const prev = cpMarks[target] || 0;
  const next = prev + mult;
  const newMarks = { ...cpMarks, [target]: next };

  let pts = 0;
  if (!oppMarks || !isClosed(oppMarks, target)) {
    const scoringMarks = Math.max(0, next - 3) - Math.max(0, prev - 3);
    pts = scoringMarks * POINT_VALUE[target];
  }

  return {
    newMarks,
    newScore: cpScore + pts,
    pts,
    closedNow: prev < 3 && next >= 3,
    lockedNow: Boolean(oppMarks && isClosed(newMarks, target) && isClosed(oppMarks, target)),
  };
};

/**
 * Condición de victoria:
 * En duelo el jugador debe igualar o superar al rival. En individual basta
 * con cerrar todos los objetivos.
 */
export const wonGame = (cp, opp) => allClosed(cp.marks) && (!opp || cp.score >= opp.score);

/* ═══════════════════════════════════════════════════════════════
   FÁBRICA DE ESTADO
═══════════════════════════════════════════════════════════════ */

let _logId = 0;

export const initState = (mode, n1, n2) => ({
  mode,
  players: mode === 'solo'
    ? [{ name: n1, score: 0, marks: createMarks(), marksTotal: 0, roundsPlayed: 0 }]
    : [
      { name: n1, score: 0, marks: createMarks(), marksTotal: 0, roundsPlayed: 0 },
      { name: n2 || 'Jugador 2', score: 0, marks: createMarks(), marksTotal: 0, roundsPlayed: 0 },
    ],
  currentPlayer: 0,
  dartsThrown: 0,   // 0–3 dardos por turno
  winner: null,     // null | 0 | 1
  log: [
    { id: _logId++, text: mode === 'solo' ? `🎯 ${n1} — Cricket individual` : `🎯 ${n1} vs ${n2} — Comienza el Cricket`, type: 'system' },
    { id: _logId++, text: `── Turno de ${n1} ──`, type: 'turn' },
  ],
});

/* ═══════════════════════════════════════════════════════════════
   COMPONENTE
═══════════════════════════════════════════════════════════════ */

const CricketGame = ({ onBack }) => {
  /* ── Setup ── */
  const [mode, setMode] = useState('duel');
  const [name1, setName1] = useState('');
  const [name2, setName2] = useState('');

  /* ── Fase ── */
  const [phase, setPhase] = useState('setup'); // 'setup' | 'playing' | 'finished'

  /* ── Modal de estadísticas ── */
  const [showStats, setShowStats] = useState(false);

  /* ── Estado de partida (un único objeto para actualizaciones atómicas) ── */
  const [gs, setGs] = useState(null);
  const [canUndo, setCanUndo] = useState(false);

  /* ── Refs ── */
  const autoEndRef = useRef(null);
  const logRef     = useRef(null);
  const gsRef      = useRef(null);
  const historyRef = useRef([]);
  const HISTORY_LIMIT = 60;

  useEffect(() => {
    gsRef.current = gs;
  }, [gs]);

  /** Guarda el estado anterior a una acción que puede deshacerse. */
  const pushHistory = useCallback(state => {
    if (!state || state.winner !== null) return false;
    historyRef.current.push(state);
    if (historyRef.current.length > HISTORY_LIMIT) historyRef.current.shift();
    setCanUndo(true);
    return true;
  }, []);

  /* ── Limpieza de timers al desmontar ── */
  useEffect(() => () => clearTimeout(autoEndRef.current), []);

  /* ── Auto-scroll del log ── */
  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  });

  /* ── Transición a pantalla de victoria cuando se detecta ganador ── */
  useEffect(() => {
    if (!gs || gs.winner === null || phase !== 'playing') return;
    clearTimeout(autoEndRef.current);
    const t = setTimeout(() => setPhase('finished'), 700);
    return () => clearTimeout(t);
  }, [gs, phase]);

  /* ── Mostrar estadísticas automáticamente al terminar la partida ── */
  useEffect(() => {
    if (phase === 'finished') setShowStats(true);
  }, [phase]);

  /* ══════════════════════════════════════
     LÓGICA DE TURNO
  ══════════════════════════════════════ */

  /** Cambia al siguiente jugador y reinicia los dardos */
  const doEndTurn = useCallback(() => {
    setGs(prev => {
      if (!prev || prev.winner !== null) return prev;
      const ci = prev.currentPlayer;
      const next = prev.mode === 'solo' ? ci : 1 - ci;
      const newPlayers = prev.players.map((p, i) =>
        i === ci ? { ...p, roundsPlayed: p.roundsPlayed + 1 } : p
      );
      const nextState = {
        ...prev,
        players: newPlayers,
        currentPlayer: next,
        dartsThrown: 0,
        log: [
          ...prev.log.slice(-49),
          { id: _logId++, text: `── Turno de ${prev.players[next].name} ──`, type: 'turn' },
        ],
      };
      gsRef.current = nextState;
      return nextState;
    });
  }, []);

  /** Auto-pasar turno tras 3 dardos */
  useEffect(() => {
    if (!gs || gs.winner !== null || gs.dartsThrown < 3) return;
    clearTimeout(autoEndRef.current);
    autoEndRef.current = setTimeout(doEndTurn, 700);
    return () => clearTimeout(autoEndRef.current);
  }, [gs, doEndTurn]);

  /* ══════════════════════════════════════
     HANDLERS
  ══════════════════════════════════════ */

  /**
   * Registra un lanzamiento de dardo.
   * @param {string|number|null} target  null = fallo
   * @param {number}             mult    1 | 2 | 3
   */
  const handleThrow = useCallback((target, mult) => {
    const currentState = gsRef.current;
    if (!currentState || currentState.winner !== null || currentState.dartsThrown >= 3) return;
    pushHistory(currentState);

    setGs(prev => {
      if (!prev || prev.winner !== null || prev.dartsThrown >= 3) return prev;

      const ci  = prev.currentPlayer;
      const cp  = prev.players[ci];
      const oi  = prev.mode === 'duel' ? 1 - ci : null;
      const opp = oi === null ? undefined : prev.players[oi];

      // Construir nuevos jugadores (shallow copy)
      const newPlayers = prev.players.map(p => ({ ...p, marks: { ...p.marks } }));
      let logText;

      if (target === null) {
        logText = `${cp.name}: Fallo 💨`;
      } else {
        const { newMarks, newScore, pts, closedNow, lockedNow } = applyThrow(
          cp.marks, cp.score, opp?.marks, target, mult
        );
        newPlayers[ci] = { ...cp, marks: newMarks, score: newScore, marksTotal: cp.marksTotal + mult };

        const multLabel = MULT_NAME[mult] || '';
        logText = `${cp.name}: ${multLabel} ${LABEL[target]}`;
        if (pts > 0)     logText += ` +${pts}`;
        if (closedNow)   logText += ' ✅';
        if (lockedNow)   logText += ' 🔒';
      }

      const newDarts = prev.dartsThrown + 1;

      // Comprobar victoria solo si hubo un hit real
      const hasWon = target !== null && wonGame(newPlayers[ci], opp);

      const logEntry = {
        id: _logId++,
        text: logText,
        type: target === null ? 'miss' : 'throw',
      };

      const nextState = {
        ...prev,
        players: newPlayers,
        dartsThrown: newDarts,
        winner: hasWon ? ci : null,
        log: [...prev.log.slice(-49), logEntry],
      };
      gsRef.current = nextState;
      return nextState;
    });
  }, [pushHistory]);

  /** Pasar turno manualmente (antes de agotar los 3 dardos) */
  const handleEndTurn = useCallback(() => {
    if (!pushHistory(gsRef.current)) return;
    clearTimeout(autoEndRef.current);
    doEndTurn();
  }, [doEndTurn, pushHistory]);

  /** Deshace la última tirada o el último fin de turno manual. */
  const handleUndo = useCallback(() => {
    if (historyRef.current.length === 0) return;
    clearTimeout(autoEndRef.current);
    const previousState = historyRef.current.pop();
    gsRef.current = previousState;
    setGs(previousState);
    setCanUndo(historyRef.current.length > 0);
  }, []);

  /** Iniciar partida desde el setup */
  const handleStart = useCallback(() => {
    _logId = 0;
    historyRef.current = [];
    setCanUndo(false);
    const n1 = name1.trim() || 'Jugador 1';
    const n2 = mode === 'duel' ? name2.trim() || 'Jugador 2' : undefined;
    const initialState = initState(mode, n1, n2);
    gsRef.current = initialState;
    setGs(initialState);
    setPhase('playing');
  }, [mode, name1, name2]);

  /** Revancha con los mismos nombres */
  const handleRematch = useCallback(() => {
    if (!gs) return;
    _logId = 0;
    historyRef.current = [];
    setCanUndo(false);
    const initialState = initState(gs.mode, gs.players[0].name, gs.players[1]?.name);
    gsRef.current = initialState;
    setGs(initialState);
    setShowStats(false);
    setPhase('playing');
  }, [gs]);

  /* ══════════════════════════════════════
     RENDER — SETUP
  ══════════════════════════════════════ */
  if (phase === 'setup') {
    return (
      <div className="cg-container">
        <div className="cg-setup">

          <button className="cg-back-btn" onClick={onBack}>← Volver</button>

          <div className="cg-setup-logo">
            <img src="/Logo26.png" alt="501 Dart League" className="cg-app-logo" />
          </div>

          <h1 className="cg-setup-title">Cricket</h1>
          <p className="cg-setup-sub">20 · 19 · 18 · 17 · 16 · 15 · Bull</p>

          <div className="cg-setup-card">
            <div className="cg-mode-selector">
              <button className={`cg-mode-option${mode === 'solo' ? ' active' : ''}`} onClick={() => setMode('solo')}>Individual</button>
              <button className={`cg-mode-option${mode === 'duel' ? ' active' : ''}`} onClick={() => setMode('duel')}>Duelo</button>
            </div>
            <label className="cg-input-label">Jugador 1</label>
            <input
              className="cg-input"
              type="text"
              value={name1}
              onChange={e => setName1(e.target.value)}
              placeholder="Jugador 1"
              maxLength={20}
              autoComplete="off"
            />
            {mode === 'duel' && <>
              <label className="cg-input-label" style={{ marginTop: 14 }}>Jugador 2</label>
              <input
                className="cg-input"
                type="text"
                value={name2}
                onChange={e => setName2(e.target.value)}
                placeholder="Jugador 2"
                maxLength={20}
                autoComplete="off"
                onKeyDown={e => e.key === 'Enter' && handleStart()}
              />
            </>}
          </div>

          <ul className="cg-rules">
            <li>🎯 Cierra los números del 20 al 15 y el Bull — hacen falta <strong>3 marcas</strong> en cada uno</li>
            <li>🔴 Simple = 1 marca · Doble = 2 marcas · Triple = 3 marcas</li>
            <li>⭕ Bull sencillo = 1 marca (25 pts) · Bullseye = 2 marcas (50 pts)</li>
            {mode === 'solo' ? <>
              <li>💰 En individual, cada marca extra sobre el cierre <strong>sigue puntuando</strong> hasta terminar la partida</li>
              <li>🏆 Ganas al cerrar todos los números</li>
            </> : <>
              <li>💰 Cada marca extra sobre el cierre <strong>puntúa</strong>, siempre que el rival no lo haya cerrado</li>
              <li>🔒 Si los dos lo cierran queda bloqueado — nadie puede seguir puntuando</li>
              <li>🏆 Gana quien cierre todos los números <strong>con igual o más puntos</strong> que el rival</li>
            </>}
          </ul>

          <button className="cg-start-btn" onClick={handleStart}>
            🎯 Iniciar Partida
          </button>
        </div>
      </div>
    );
  }

  /* ══════════════════════════════════════
     RENDER — PLAYING
  ══════════════════════════════════════ */
  if (phase === 'playing' && gs) {
    const p0 = gs.players[0];
    const p1 = gs.players[1];
    const cp   = gs.players[gs.currentPlayer];
    const opp  = gs.mode === 'duel' ? gs.players[1 - gs.currentPlayer] : undefined;
    const done = gs.dartsThrown >= 3 || gs.winner !== null;

    // Filas del tablero (compartidas entre columna derecha desktop y vista móvil)
    const renderBoardRows = () => TARGETS.map(target => {
      const m0     = p0.marks[target] || 0;
      const m1     = p1?.marks[target] || 0;
      const locked = Boolean(p1 && isLocked(p0.marks, p1.marks, target));
      const c0     = isClosed(p0.marks, target);
      const c1     = isClosed(p1.marks, target);
      return (
        <div key={String(target)} className={`cg-row${locked ? ' cg-row-locked' : ''}`}>
          <div className="cg-mark-cell">
            <span className={`cg-mark ${c0 ? 'closed' : m0 > 0 ? 'open' : 'empty'}`}>
              {markSymbol(m0) || '○'}
            </span>
          </div>
          <div className={`cg-num-label${locked ? ' locked' : ''}`}>
            {locked && <span className="cg-lock-icon">🔒</span>}
            {LABEL[target]}
          </div>
          <div className="cg-mark-cell right">
            <span className={`cg-mark ${c1 ? 'closed' : m1 > 0 ? 'open' : 'empty'}`}>
              {markSymbol(m1) || '○'}
            </span>
          </div>
        </div>
      );
    });

    return (
      <div className="cg-container">
        <div className={`cg-playing${gs.mode === 'solo' ? ' cg-playing-solo' : ''}`}>
          <button className="cg-back-btn" onClick={onBack}>← Volver</button>

          <div className="cg-play-layout">

            {/* ════ COLUMNA PRINCIPAL ════ */}
            <div className="cg-play-main">

              {/* ── Tarjetas con marcas integradas ── */}
              <div className={`cg-header${gs.mode === 'solo' ? ' cg-header-solo' : ''}`}>
                {gs.players.map((p, i) => {
                  const isActive = gs.currentPlayer === i;
                  const playerMpr = calculateMpr(p, isActive ? gs.dartsThrown : 0);
                  return (
                    <div key={i} className={`cg-player-card${isActive ? ' active' : ''}`}>
                      <div className="cg-pc-row">
                        <div className="cg-pc-name-row">
                          <div className="cg-pc-name">
                            <span className="cg-pc-name-text">{p.name}</span>
                            <span className="cg-pc-meta">
                              <span className="cg-pc-round">R{p.roundsPlayed + 1}</span>
                              <span className="cg-pc-mpr">MPR {playerMpr}</span>
                            </span>
                          </div>
                          {isActive && (
                            <span className="cg-pc-turn-tag">¡TU TURNO!</span>
                          )}
                        </div>
                        <div className="cg-pc-score">{p.score}</div>
                      </div>
                      <div className="cg-pc-marks">
                        {TARGETS.map(target => {
                          const m  = p.marks[target] || 0;
                          const c  = isClosed(p.marks, target);
                          const lk = Boolean(p1 && isLocked(gs.players[0].marks, p1.marks, target));
                          return (
                            <div key={String(target)} className={`cg-pc-mcell${lk ? ' lk' : ''}`}>
                              <span className="cg-pc-mnum">{LABEL[target]}</span>
                              <span className={`cg-pc-msym ${c ? 'closed' : m > 0 ? 'open' : 'empty'}`}>
                                {markSymbol(m) || '·'}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* ── Barra de turno ── */}
              <div className="cg-turn-bar">
                <div>
                  <div className="cg-turn-label">Turno</div>
                  <div className="cg-turn-name">{cp.name}</div>
                </div>
                <div className="cg-darts-row">
                  {[0, 1, 2].map(i => (
                    <div key={i} className={`cg-dart-pip${i < gs.dartsThrown ? ' used' : ''}`} />
                  ))}
                </div>
              </div>

              {/* ── Panel de lanzamientos ── */}
              <div className="cg-throw-panel">
                <div className="cg-panel-header">
                  <div className="cg-panel-label">Registrar lanzamiento</div>
                  <button
                    className="cg-undo-btn"
                    onClick={handleUndo}
                    disabled={!canUndo}
                    aria-label="Deshacer última acción"
                  >
                    ↩ Deshacer
                  </button>
                </div>

                <div className="cg-throw-grid">
                  {/* ── Grupo de fallos — primero, es el más usado ── */}
                  <div className="cg-group cg-miss-group">
                    <div className="cg-group-label cg-miss-label">MISS</div>
                    <div className="cg-group-btns">
                      <button
                        className="cg-btn-throw cg-tc-miss"
                        onClick={() => handleThrow(null, 0)}
                        disabled={done}
                        title="Fallo: un dardo que no da en ningún número válido"
                      >
                        FALLO
                      </button>
                      <button
                        className="cg-btn-throw cg-tc-end"
                        onClick={handleEndTurn}
                        title="Fin de turno: todos los dardos restantes fallados"
                      >
                        TODOS FALLADOS
                      </button>
                    </div>
                  </div>

                  {TARGETS.map(target => {
                    const locked   = Boolean(opp && isLocked(cp.marks, opp.marks, target));
                    const cpClosed = isClosed(cp.marks, target);
                    const scoring  = cpClosed && !locked;
                    const isBull   = target === 'bull';
                    const mults    = isBull ? [2, 1] : [3, 2, 1];
                    const disabled = locked || done;

                    let groupCls = 'cg-group';
                    if (isBull)        groupCls += ' cg-bull';
                    if (locked)        groupCls += ' cg-locked';
                    else if (done)     groupCls += ' cg-done';
                    else if (scoring)  groupCls += ' cg-scoring';

                    let labelCls = 'cg-group-label';
                    if (scoring)        labelCls += ' scoring';
                    else if (cpClosed)  labelCls += ' closed';

                    return (
                      <div key={String(target)} className={groupCls}>
                        <div className={labelCls}>{LABEL[target]}</div>
                        <div className="cg-group-btns">
                          {mults.map(m => (
                            <button
                              key={m}
                              className={`cg-btn-throw cg-tc-${m === 1 ? BOARD_COLOR[target].single : BOARD_COLOR[target].multi}`}
                              onClick={() => handleThrow(target, m)}
                              disabled={disabled}
                              title={
                                isBull
                                  ? (m === 1
                                    ? 'Bull sencillo · 1 marca · 25 pts si puntúa'
                                    : 'Bullseye · 2 marcas · 50 pts si puntúa')
                                  : `${MULT_NAME[m]} ${LABEL[target]} · ${m} marca${m > 1 ? 's' : ''}`
                              }
                            >
                              {m === 1 ? 'S' : m === 2 ? 'D' : 'T'}
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* ── Log de partida ── */}
              <div className="cg-log" ref={logRef}>
                {gs.log.map(entry => (
                  <div key={entry.id} className={`cg-log-entry cg-log-${entry.type}`}>
                    {entry.text}
                  </div>
                ))}
              </div>

            </div>{/* fin cg-play-main */}

            {/* ════ COLUMNA DERECHA: tablero completo (solo escritorio) ════ */}
            {p1 && <div className="cg-play-board">
              <div className="cg-board">
                <div className="cg-board-header">
                  <span className="cg-bh-name">{p0.name}</span>
                  <span>Nº</span>
                  <span className="right cg-bh-name">{p1.name}</span>
                </div>
                {renderBoardRows()}
              </div>
              {/* MPR bajo el tablero */}
              <div className="cg-board-footer">
                <div className={`cg-bf-side${gs.currentPlayer === 0 ? ' active' : ''}`}>
                  <div className="cg-bf-score">{calculateMpr(p0)}</div>
                  <div className="cg-bf-label">MPR</div>
                </div>
                <div className="cg-bf-sep">vs</div>
                <div className={`cg-bf-side${gs.currentPlayer === 1 ? ' active' : ''}`}>
                  <div className="cg-bf-score">{calculateMpr(p1)}</div>
                  <div className="cg-bf-label">MPR</div>
                </div>
              </div>
            </div>}

          </div>{/* fin cg-play-layout */}
        </div>
      </div>
    );
  }

  /* ══════════════════════════════════════
     RENDER — FINISHED
  ══════════════════════════════════════ */
  if (phase === 'finished' && gs) {
    const winIdx = gs.winner;
    const winner = gs.players[winIdx];
    const loser  = gs.players.find((_, index) => index !== winIdx);
    const mprFinal = (p, i) => {
      const partialTurnDarts = i === winIdx ? gs.dartsThrown : 0;
      return calculateMpr(p, partialTurnDarts);
    };

    return (
      <div className="cg-container">
        <div className="cg-win">
          <div className="cg-win-trophy">🏆</div>
          <div className="cg-win-label">¡Ganador!</div>
          <div className="cg-win-name">{winner.name}</div>
          <div className="cg-win-scores">
            <span className="w">{winner.name}: {winner.score} pts</span>
            {loser && <span className="l">{loser.name}: {loser.score} pts</span>}
          </div>
          <div className="cg-win-actions">
            <button className="cg-btn-back" onClick={onBack}>← Menú</button>
            <button className="cg-btn-stats" onClick={() => setShowStats(true)}>📊 Stats</button>
            <button className="cg-btn-rematch" onClick={handleRematch}>Revancha 🔄</button>
          </div>
        </div>

        {showStats && (
          <div className="cg-stats-overlay" onClick={() => setShowStats(false)}>
            <div className="cg-stats-modal" onClick={e => e.stopPropagation()}>
              <img src="/Logo26.png" alt="501 Dart League" className="cg-stats-logo" />
              <div className="cg-stats-title">📊 Estadísticas de partida</div>
              <div className="cg-stats-grid">
                {gs.players.map((p, i) => (
                  <div key={i} className={`cg-stats-player${i === winIdx ? ' winner' : ''}`}>
                    {i === winIdx && <span className="cg-stats-crown" aria-label="Ganador">👑</span>}
                    <div className="cg-stats-pname">
                      {p.name}
                    </div>
                    <div className="cg-stats-ppd-val">{mprFinal(p, i)}</div>
                    <div className="cg-stats-ppd-lbl">MPR · marcas por turno</div>
                    <div className="cg-stats-rows">
                      <div className="cg-stats-row">
                        <span className="cg-sr-val">{p.marksTotal}</span>
                        <span className="cg-sr-lbl">marcas</span>
                      </div>
                      <div className="cg-stats-row">
                        <span className="cg-sr-val">{p.roundsPlayed}</span>
                        <span className="cg-sr-lbl">turnos</span>
                      </div>
                      <div className="cg-stats-row">
                        <span className="cg-sr-val">{p.score}</span>
                        <span className="cg-sr-lbl">puntos</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="cg-stats-actions">
                <button className="cg-btn-back" onClick={onBack}>← Menú</button>
                <button className="cg-btn-rematch" onClick={handleRematch}>Revancha 🔄</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return null;
};

export default CricketGame;
